import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CRow, CCol, CCard, CCardBody, CButton } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPlus, cilBuilding } from '@coreui/icons'
import AddForm from './AddForm'
import EditForm from './EditForm'
import SponsorService from 'src/service/SponsorService'
import AdminSponsorTable from './AdminSponsorTable'

const SponsorsPanel = ({ embedded = false, onTotalChange }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState({})
  const [addSponsorVisible, setAddSponsorVisible] = useState(false)
  const [editSponsorId, setEditSponsorId] = useState(null)

  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    params.append('sponsor_scope', 'hub')
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    try {
      const result = await SponsorService.getSponsor(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
      onTotalChangeRef.current?.(result.data?.total)
    } catch (error) {
      console.error('Error fetching sponsors:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnFilter, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        {!embedded && (
          <div>
            <h4 className="mb-1">Sponsor Directory</h4>
            <div className="text-medium-emphasis">
              Manage the sponsors available to your games, their logos and links.
            </div>
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 ms-auto">
          <CButton color="primary" onClick={() => setAddSponsorVisible(true)}>
            <CIcon icon={cilPlus} className="me-1" />
            Add Sponsor
          </CButton>
        </div>
      </div>

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Total Sponsors</div>
                <div className="fs-4 fw-semibold">{users?.total ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilBuilding} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <AdminSponsorTable
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnFilter={setColumnFilter}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnFilter={columnFilter}
            columnSorter={columnSorter}
            loading={loading}
            onEditSponsor={setEditSponsorId}
          />
        </CCardBody>
      </CCard>

      <AddForm
        visible={addSponsorVisible}
        onClose={() => setAddSponsorVisible(false)}
        fetchUsers={fetchUsers}
      />

      <EditForm
        sponsorId={editSponsorId}
        visible={!!editSponsorId}
        onClose={() => setEditSponsorId(null)}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default SponsorsPanel
